import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gift, Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Footer referral block — "Refer a friend, earn $100 when they book."
 *
 * Signed-out: shows the value prop + a "Sign in to get your code" CTA.
 * Signed-in: lazily fetches the user's referral code + cumulative credits,
 * shows a copy-able JT-REF-XXXX code, and offers Email / SMS / share-sheet
 * channels. Designed to live in the Footer concierge column with the same
 * `lux-card`-adjacent vocabulary the rest of the footer uses.
 */
export default function ReferralBox() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) {
      setInfo(null);
      return;
    }
    let mounted = true;
    api
      .get("/me/referral")
      .then((r) => {
        if (mounted) setInfo(r.data);
      })
      .catch(() => {
        /* silent — block falls back to the signed-out CTA */
      });
    return () => {
      mounted = false;
    };
  }, [user]);

  const copyCode = async () => {
    if (!info?.referral_code) return;
    try {
      await navigator.clipboard.writeText(info.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Referral code copied.");
    } catch {
      toast.message("Copy unavailable — long-press the code to copy.");
    }
  };

  const share = async () => {
    if (!info?.share_url) return;
    const text = `Join me on Janté Travel Concierges — Sasha plans, the concierge books. Use my code ${info.referral_code} and we both win.`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Janté Travel Concierges",
          text,
          url: info.share_url,
        });
        return;
      } catch {
        /* user dismissed the share sheet — fall through to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${info.share_url}`);
      toast.success("Share link copied.");
    } catch {
      toast.message("Long-press the link below to copy.");
    }
  };

  if (!user) {
    // Signed-out treatment — small recruitment pitch.
    return (
      <div
        className="mt-6 border-t border-[var(--gold)]/30 pt-5"
        data-testid="footer-referral-signed-out"
      >
        <div className="overline flex items-center gap-2 text-[var(--gold)]">
          <Gift size={11} strokeWidth={1.5} /> {t("footer.referral.heading")}
        </div>
        <p className="text-xs text-[var(--ink-2)] leading-relaxed mt-2">
          {t("footer.referral.pitchSignedOut")}
        </p>
        <Link
          to="/register"
          className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[var(--brand-red)] hover:text-[var(--emerald)] transition-colors"
          data-testid="footer-referral-signed-out-cta"
        >
          {t("footer.referral.ctaSignedOut")}
        </Link>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="mt-6 border-t border-[var(--gold)]/30 pt-5 text-[11px] text-[var(--muted)]">
        Loading referral…
      </div>
    );
  }

  return (
    <div
      className="mt-6 border-t border-[var(--gold)]/30 pt-5"
      data-testid="footer-referral-box"
    >
      <div className="overline flex items-center gap-2 text-[var(--gold)]">
        <Gift size={11} strokeWidth={1.5} /> {t("footer.referral.heading")}
      </div>
      <p className="text-xs text-[var(--ink-2)] leading-relaxed mt-2">
        {t("footer.referral.pitchSignedIn")}
      </p>
      <div className="mt-3 flex items-center gap-2 border border-[var(--gold)] px-3 py-2">
        <span
          className="font-mono text-sm tracking-[0.18em] text-[var(--emerald)] flex-1 truncate"
          data-testid="footer-referral-code"
        >
          {info.referral_code}
        </span>
        <button
          type="button"
          onClick={copyCode}
          className="text-[var(--gold)] hover:text-[var(--brand-red)] transition-colors"
          aria-label="Copy referral code"
          data-testid="footer-referral-copy"
        >
          {copied ? <Check size={14} strokeWidth={1.75} /> : <Copy size={13} strokeWidth={1.5} />}
        </button>
        <button
          type="button"
          onClick={share}
          className="text-[var(--gold)] hover:text-[var(--brand-red)] transition-colors"
          aria-label="Share referral link"
          data-testid="footer-referral-share"
        >
          <Share2 size={13} strokeWidth={1.5} />
        </button>
      </div>
      {info.credits_usd > 0 && (
        <div
          className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[var(--brand-red)]"
          data-testid="footer-referral-credits"
        >
          {t(
            info.referral_count === 1
              ? "footer.referral.credits"
              : "footer.referral.creditsPlural",
            { amount: info.credits_usd.toFixed(0), count: info.referral_count },
          )}
        </div>
      )}
    </div>
  );
}
